import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { imgUrl } from '../utils/api-request';
import MovieControlButtons from './MovieControlButtons';
import usePlayback from '../hooks/usePlayback';

const MovieControl = ({ playing, movieId, orderId }) => {
    const {
        position,
        duration,
        playStatus,
        isFinished,
        isLoading,
        isDragging,
        isLocked,
        autoEjectIn,
        error,
        pause,
        resume,
        stop,
        seek,
        refreshStatus,
        setDragging,
        setLocalPosition,
        clearError,
    } = usePlayback(orderId, movieId);

    useEffect(() => {
        if (error) {
            toast.error(error);
            clearError();
        }
    }, [error, clearError]);

    const [playingCursor, setPlayingCursor] = useState(position || 0);
    const [finishCountdown, setFinishCountdown] = useState(null);
    const progressBarRef = useRef(null);

    // Sync cursor from hook
    useEffect(() => {
        if (!isDragging) {
            setPlayingCursor(position);
        }
    }, [position, isDragging]);

    // Handle finished state with auto-close countdown
    useEffect(() => {
        if (isFinished) {
            setFinishCountdown(5);
            const countdownInterval = setInterval(() => {
                setFinishCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(countdownInterval);
                        stop();
                        return null;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(countdownInterval);
        }
    }, [isFinished, stop]);

    // Fetch initial status
    useEffect(() => {
        refreshStatus();
    }, [refreshStatus]);

    const handlePlay = async () => {
        await resume();
    };

    const handlePause = async () => {
        await pause();
    };

    const handleStop = async () => {
        await stop();
    };

    const handleSeek = async (newTime) => {
        await seek(newTime);
    };

    const handleJump = async (direction, seconds) => {
        const newTime = direction === 'backward' ? position - seconds : position + seconds;
        await seek(newTime);
    };

    const calculatePercentage = () => {
        if (duration) {
            return Math.floor((playingCursor / duration) * 100);
        }
        return 0;
    };

    const formatTime = (seconds) => {
        if (isNaN(seconds) || seconds === null || seconds === undefined) {
            return '--:--:--';
        }

        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = Math.floor(seconds % 60);

        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
    };

    const formatCountdown = (seconds) =>
        `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;

    const handleDragStart = () => {
        setDragging(true);
    };

    const handleDragMove = (event) => {
        if (!isDragging) return;

        const progressBar = progressBarRef.current;
        if (!progressBar) return;

        const rect = progressBar.getBoundingClientRect();
        const clientX = event.touches ? event.touches[0].clientX : event.clientX;
        const offsetX = Math.max(0, Math.min(clientX - rect.left, rect.width));
        const newTime = (offsetX / rect.width) * duration;

        setPlayingCursor(newTime);
        setLocalPosition(newTime);
    };

    const handleDragEnd = async () => {
        setDragging(false);
        if (playingCursor !== null) {
            await handleSeek(playingCursor);
        }
    };

    // Locked either by this client's own in-flight request or by another client's
    // control action broadcast via playback:lock.
    const controlsLocked = isLoading || isLocked;

    const handleDragEvents = eventHandler => controlsLocked ? undefined : eventHandler;

    return (
        <div className="player-container">
            <div className="ps-5 pt-4 d-flex justify-content-start gap-3">
                <div>
                    {playing.movie.poster && (
                        <img
                            className="logo-img"
                            width={75}
                            alt={playing.movie.title}
                            src={imgUrl(playing.movie.poster.imageUrl)}
                        />
                    )}
                </div>

                <div className="flex-grow-1">
                    <div className="mt-3 d-flex justify-content-between">
                        <h4 className="mb-0 text-primary">{playing.movie.title}</h4>
                    </div>

                    {isFinished ? (
                        <div className="py-4 text-center">
                            <div className="text-success mb-2">
                                <span className="fas fa-check-circle me-2" />
                                Playback complete
                            </div>
                            <small className="text-muted">
                                Closing in {finishCountdown} second{finishCountdown !== 1 ? 's' : ''}...
                            </small>
                        </div>
                    ) : (
                        <>
                            {autoEjectIn !== null && (
                                <div className="alert alert-warning d-flex align-items-center gap-2 py-2 px-3 my-2">
                                    <span className="fas fa-triangle-exclamation" />
                                    <span className="small">
                                        Paused too long — the content will be ejected in{' '}
                                        <strong>{formatCountdown(autoEjectIn)}</strong>. Press play to keep it loaded.
                                    </span>
                                </div>
                            )}

                            <MovieControlButtons
                                playingCursor={playingCursor}
                                playStatus={playStatus}
                                handlePlay={handlePlay}
                                handlePause={handlePause}
                                handleStop={handleStop}
                                handleJump={handleJump}
                                lockControls={controlsLocked}
                            />

                            {duration > 0 && (
                                <div className="pe-3">
                                    <div className="d-flex justify-content-between small text-center">
                                        <span>{formatTime(playingCursor)}</span>
                                        <span>{formatTime(duration)}</span>
                                    </div>
                                    <div
                                        ref={progressBarRef}
                                        className={`progress-bar-container position-relative w-100`}
                                        style={{
                                            height: '7px',
                                            backgroundColor: '#555',
                                            cursor: controlsLocked ? 'not-allowed' : 'pointer',
                                            pointerEvents: controlsLocked ? 'none' : 'auto',
                                        }}
                                        onMouseDown={handleDragEvents(handleDragStart)}
                                        onMouseMove={handleDragEvents(handleDragMove)}
                                        onMouseUp={handleDragEvents(handleDragEnd)}
                                        onTouchStart={handleDragEvents(handleDragStart)}
                                        onTouchMove={handleDragEvents(handleDragMove)}
                                        onTouchEnd={handleDragEvents(handleDragEnd)}
                                    >
                                        <div
                                            className="progress-bar bg-success striped-animated"
                                            style={{
                                                position: 'absolute',
                                                height: '100%',
                                                width: `${calculatePercentage()}%`,
                                            }}
                                        />
                                        <div
                                            className="progress-thumb position-absolute bg-white"
                                            style={{
                                                left: `${calculatePercentage()}%`,
                                                transform: 'translateX(-50%) translateY(-30%)',
                                                width: '20px',
                                                height: '20px',
                                                border: '2px solid green',
                                                borderRadius: '50%',
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MovieControl;
